/**
 * useAnalyticsChat - React Hook for Analytics Chat Session Management
 *
 * Manages persistent analytics chat sessions via the /api/analytics-chat REST API.
 * Provides session CRUD, turn persistence callbacks for useAgentChat, and
 * auto-titling of new sessions from the first AI response.
 *
 * @see plan.md P1.5
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = '/api/analytics-chat';

/**
 * Extract a short title from the user's first message.
 * Grabs the first meaningful sentence or phrase (up to 60 chars).
 */
function extractTitle(content) {
  if (!content || typeof content !== 'string') return 'Untitled Session';
  const clean = content
    .replace(/[#*_~`>]/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
  const firstSentence = clean.split(/[.!?\n]/)[0]?.trim() || '';
  if (firstSentence.length > 60) return firstSentence.slice(0, 57) + '...';
  return firstSentence || 'Analytics Session';
}

export function useAnalyticsChat({ userId, sportContext } = {}) {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Turns loaded from a past session — consumer maps to chat messages format
  const [loadedTurns, setLoadedTurns] = useState(null);

  // Track whether the active session has been auto-titled
  const hasAutoTitledRef = useRef(false);
  // Track turn count in active session for auto-title trigger
  const turnCountRef = useRef(0);

  /**
   * List all sessions for the current user
   */
  const listSessions = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/sessions`, {
        headers: { 'x-user-id': userId },
      });
      if (!res.ok) throw new Error(`Failed to list sessions: ${res.status}`);
      const json = await res.json();
      const data = json.data || json;
      // Sort by updated_at desc
      const sorted = (data.sessions || []).sort(
        (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
      );
      setSessions(sorted);
    } catch (err) {
      console.error('[useAnalyticsChat] listSessions error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Create a new session
   */
  const createSession = useCallback(async (title) => {
    if (!userId) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({
          title: title || 'New Analytics Session',
          sportContext,
        }),
      });
      if (!res.ok) throw new Error(`Failed to create session: ${res.status}`);
      const json = await res.json();
      const session = json.data || json;
      // Set as active
      const newSession = {
        ...session,
        turns: [],
        metadata: session.metadata || {},
      };
      setActiveSession(newSession);
      setLoadedTurns(null); // New session — no history to restore
      hasAutoTitledRef.current = false;
      turnCountRef.current = 0;
      // Refresh session list
      await listSessions();
      return newSession;
    } catch (err) {
      console.error('[useAnalyticsChat] createSession error:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, sportContext, listSessions]);

  /**
   * Load a session by ID (with turns)
   */
  const loadSession = useCallback(async (sessionId) => {
    if (!sessionId) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}`, {
        headers: { 'x-user-id': userId },
      });
      if (!res.ok) throw new Error(`Failed to load session: ${res.status}`);
      const json = await res.json();
      const session = json.data || json;
      const loaded = {
        ...session,
        turns: session.turns || [],
        metadata: session.metadata || {},
      };
      setActiveSession(loaded);
      setLoadedTurns(loaded.turns || []); // Expose turns so parent can restore chat messages
      hasAutoTitledRef.current = true; // Existing session already has a title
      turnCountRef.current = (loaded.turns || []).length;
      return loaded;
    } catch (err) {
      console.error('[useAnalyticsChat] loadSession error:', err);
      setError('Could not load session');
      // Fall back to new session (Ned's error state requirement)
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Delete a session (soft delete)
   */
  const deleteSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId },
      });
      if (!res.ok) throw new Error(`Failed to delete session: ${res.status}`);
      // If active session was deleted, clear it
      if (activeSession?.session_id === sessionId) {
        setActiveSession(null);
      }
      // Refresh list
      await listSessions();
    } catch (err) {
      console.error('[useAnalyticsChat] deleteSession error:', err);
      setError(err.message);
    }
  }, [userId, activeSession, listSessions]);

  /**
   * Update session title
   */
  const updateSessionTitle = useCallback(async (sessionId, title) => {
    if (!sessionId || !title) return;
    try {
      const res = await fetch(`${API_BASE}/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error(`Failed to update title: ${res.status}`);
      // Update local state
      setActiveSession(prev =>
        prev?.session_id === sessionId ? { ...prev, title } : prev
      );
      setSessions(prev =>
        prev.map(s => s.session_id === sessionId ? { ...s, title } : s)
      );
    } catch (err) {
      console.error('[useAnalyticsChat] updateSessionTitle error:', err);
      // Non-blocking — title update failure is not critical
    }
  }, [userId]);

  /**
   * Callback for useAgentChat — updates local state and triggers auto-title.
   * Turn persistence is handled on the backend (conversationStateManager → chatPersistenceService).
   */
  const onTurnComplete = useCallback((turn) => {
    if (!activeSession?.session_id) return;

    const sessionId = activeSession.session_id;

    // Update local active session turns
    setActiveSession(prev => {
      if (!prev || prev.session_id !== sessionId) return prev;
      return {
        ...prev,
        turns: [...(prev.turns || []), turn],
        updated_at: new Date().toISOString(),
      };
    });

    turnCountRef.current += 1;

    // Auto-title: use the user's first message as the session title
    if (
      turn.role === 'user' &&
      !hasAutoTitledRef.current &&
      turnCountRef.current <= 2
    ) {
      hasAutoTitledRef.current = true;
      const title = extractTitle(turn.content);
      if (title && title !== 'Untitled Session') {
        updateSessionTitle(sessionId, title);
      }
    }

    // Refresh session metadata (patterns) after assistant responses
    if (turn.role === 'assistant') {
      fetch(`${API_BASE}/sessions/${sessionId}`, {
        headers: { 'x-user-id': userId },
      })
        .then(res => res.ok ? res.json() : null)
        .then(json => {
          const data = json?.data || json;
          if (data?.metadata) {
            setActiveSession(prev =>
              prev?.session_id === sessionId ? { ...prev, metadata: data.metadata } : prev
            );
          }
        })
        .catch(() => {});
    }
  }, [activeSession, userId, updateSessionTitle]);

  // Auto-load session list on mount when userId is available
  useEffect(() => {
    if (userId) {
      listSessions();
    }
  }, [userId, listSessions]);

  return {
    sessions,
    activeSession,
    loading,
    error,
    loadedTurns,
    createSession,
    loadSession,
    deleteSession,
    updateSessionTitle,
    listSessions,
    onTurnComplete,
  };
}
